import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PushButton } from "../components/PushButton";
import { Footer } from "../components/Footer";
import { JitsiRoom } from "../components/JitsiRoom";
import { BiometricLock, BiometricToggle } from "../components/PortalBiometric";
import { FeedbackPrompt, StarRating } from "../components/FeedbackPrompt";
import { biometricSupported, biometricAvailable, isBiometricEnabled } from "../lib/biometric";
import { PatientRequests, PatientMessages, NotificationPrefs, PatientTraining, PatientGroupCalls, MyDetailsCard, ChangePasswordCard, PasswordResetPrompt } from "../components/PatientExtras";
import { fmtAppt, fmtApptTime, apptStartMs } from "../lib/datetime";
import { TimezoneCard } from "../components/TimezoneCard";
import { ConfidentialityGate } from "../components/ConfidentialityGate";
import { InterpreterAssignments, InterpreterAvailability, TranslateTool } from "../components/InterpreterExtras";
import { useT } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { toast } from "sonner";
import { HeartPulse, Video, Phone, MessageSquare, LogOut, CalendarClock, IdCard, GraduationCap, Inbox, ShieldCheck, Settings, User, KeyRound, Clock, Languages, ArrowLeftRight } from "lucide-react";

const MODE_ICON = { video: Video, audio: Phone, text: MessageSquare };

const PORTAL_TABS = [
  { k: "appointments", label: "My Appointments", icon: CalendarClock },
  { k: "learn", label: "Learn & Training", icon: GraduationCap },
  { k: "inbox", label: "Inbox", icon: Inbox },
  { k: "details", label: "My Details", icon: User },
  { k: "password", label: "Change Password", icon: KeyRound },
  { k: "settings", label: "Settings", icon: Settings },
];

const INTERP_TABS = [
  { k: "assignments", label: "My Assignments", icon: CalendarClock },
  { k: "availability", label: "My Availability", icon: Clock },
  { k: "translate", label: "Translate", icon: ArrowLeftRight },
  { k: "inbox", label: "Inbox", icon: Inbox },
  { k: "details", label: "My Details", icon: User },
  { k: "password", label: "Change Password", icon: KeyRound },
  { k: "settings", label: "Settings", icon: Settings },
];

export default function Portal({ consultId }) {
  const { user, logout } = useAuth();
  const { t: tr } = useT();
  const isInterp = user?.role === "language_interpreter";
  const TABS = isInterp ? INTERP_TABS : PORTAL_TABS;
  const [appts, setAppts] = useState([]);
  const [join, setJoin] = useState(null);
  const [locked, setLocked] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const [welcome, setWelcome] = useState(false);
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState(isInterp ? "assignments" : "appointments");
  const [unread, setUnread] = useState(0);
  const attemptJoin = async (appt) => {
    try {
      const { data } = await api.get(`/appointments/${appt.id}/clinician-status`);
      if (data.available) { setBusy(null); setJoin(appt.id); }
      else setBusy({ appt, message: data.message });
    } catch { setJoin(appt.id); }
  };
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/appointments/${busy.appt.id}/clinician-status`);
        if (data.available) { clearInterval(t); toast.success("The clinician is now available — joining you in…"); const id = busy.appt.id; setBusy(null); setJoin(id); }
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [busy]);
  useEffect(() => {
    try { if (sessionStorage.getItem("rc_welcome")) { setWelcome(true); sessionStorage.removeItem("rc_welcome"); } } catch { /* storage off */ }
  }, []);

  const load = async () => { try { setAppts((await api.get("/me/appointments")).data); } catch (error) { console.error("Failed to load appointments:", error); } };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const loadUnread = async () => { try { const { data } = await api.get("/me/messages/unread"); setUnread(data.unread || 0); } catch { /* ignore */ } };
    loadUnread();
    const t = setInterval(loadUnread, 15000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (tab === "inbox") setUnread(0); }, [tab]);
  const consultTriedRef = useRef(false);
  useEffect(() => {
    if (consultId && !consultTriedRef.current && !locked) {
      consultTriedRef.current = true;
      attemptJoin({ id: consultId });
    }
  }, [consultId, locked]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    (async () => {
      if (biometricSupported() && isBiometricEnabled() && await biometricAvailable()) setLocked(true);
    })();
  }, []);

  const soon = appts.find((a) => {
    if (a.status !== "scheduled") return false;
    const start = apptStartMs(a);
    if (!start) return false;
    const dur = (a.duration || 30) * 60000;
    return nowTs >= start - 15 * 60000 && nowTs <= start + dur;
  });
  const soonMins = soon ? Math.round((apptStartMs(soon) - nowTs) / 60000) : 0;

  const checkedRef = useRef(null);
  useEffect(() => {
    if (soon?.id && checkedRef.current !== soon.id) {
      checkedRef.current = soon.id;
      api.post(`/appointments/${soon.id}/check-in`).catch(() => {});
    }
  }, [soon?.id]);

  if (locked) return <BiometricLock onUnlocked={() => setLocked(false)} />;

  return (
    <div className="min-h-screen bg-sand">
      <ConfidentialityGate />
      <header className="bg-clinical text-white" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex flex-col leading-none">
            <span className="flex items-center gap-2 font-heading font-black text-xl"><HeartPulse className="w-6 h-6 text-alert" /> Hutanohwevanhu.org</span>
            <span className="text-[11px] font-semibold text-white/70 tracking-tight mt-0.5 pl-8">Rural Health Promotion &amp; Telemedicine</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <PushButton userType="public" variant="dark" />
            <button onClick={logout} data-testid="portal-logout" className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-md text-sm"><LogOut className="w-4 h-4" /> {tr("Logout")}</button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-10">
        <PasswordResetPrompt />
        {soon && !join && (
          <div data-testid="join-now-banner" className="mb-6 rounded-xl bg-critical text-white px-5 py-4 flex flex-wrap items-center justify-between gap-3 shadow-lg ring-1 ring-critical/40">
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/70 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
              </span>
              <div>
                <p className="font-heading font-bold" data-testid="join-now-text">
                  {soonMins > 0 ? `${tr("Your consultation starts in")} ${soonMins} ${tr("min")}` : tr("Your consultation is ready")}
                </p>
                <p className="text-white/85 text-sm">{soon.specialist_name} · {soon.mode} · {fmtApptTime(soon)}</p>
              </div>
            </div>
            <button onClick={() => attemptJoin(soon)} data-testid="join-now-btn" className="bg-white text-critical font-semibold rounded-md px-5 py-2 inline-flex items-center gap-2 hover:bg-white/90 transition-colors">
              <Video className="w-4 h-4" /> {tr("Join now")}
            </button>
          </div>
        )}
        {welcome && (
          <div data-testid="welcome-banner" className="mb-5 flex items-center justify-between gap-3 bg-calm/10 border border-calm/30 text-calm rounded-lg px-4 py-3">
            <span className="text-sm font-semibold">✅ {tr("Welcome to your patient portal. Your account is active and ready to use.")}</span>
            <button onClick={() => setWelcome(false)} data-testid="welcome-banner-dismiss" className="text-calm/70 hover:text-calm text-lg leading-none">×</button>
          </div>
        )}
        <h1 className="font-heading font-bold text-3xl tracking-tight text-ink">{tr("Welcome")}, {user?.name}</h1>
        {!isInterp && (
          <div className="mt-4 inline-flex items-center gap-2 bg-white border border-line rounded-full px-4 py-2 text-sm" data-testid="portal-code">
            <IdCard className="w-4 h-4 text-clinical" /> {tr("Your appointment code:")} <span className="font-heading font-bold text-clinical">{user?.appointment_code || "—"}</span>
          </div>
        )}
        {isInterp && (
          <div className="mt-4 inline-flex items-center gap-2 bg-clinical/10 border border-clinical/30 text-clinical rounded-full px-4 py-2 text-sm font-semibold" data-testid="interpreter-badge">
            <Languages className="w-4 h-4" /> {tr("Language Interpreter")}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-7 mb-8 border-b border-line" data-testid="portal-tabs">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`portal-tab-${t.k}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.k ? "border-clinical text-clinical" : "border-transparent text-muted2 hover:text-ink"}`}>
              <t.icon className="w-4 h-4" /> {tr(t.label)}
              {t.k === "inbox" && unread > 0 && (
                <span data-testid="portal-inbox-badge" className="text-xs font-bold bg-critical text-white rounded-full px-2 min-w-[20px] text-center">{unread}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "appointments" && (
          <div data-testid="portal-panel-appointments">
            <p className="text-muted2 text-sm">{tr("Share your appointment code with the clinic so they can schedule your virtual consultations. Enable alerts to get reminders.")}</p>
            <BiometricToggle />
            <h2 className="font-heading font-bold text-xl text-ink mt-8 mb-4 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-clinical" /> {tr("My appointments")}</h2>
            <div className="grid gap-3" data-testid="portal-appointments">
              {appts.map((a) => {
                const MI = MODE_ICON[a.mode] || Video;
                return (
                  <div key={a.id} data-testid="portal-appt-row" className="bg-white border border-line rounded-md p-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <span className="font-heading font-bold text-ink">{a.specialist_name}</span>
                      <p className="text-sm text-muted2 mt-1 inline-flex items-center gap-2"><MI className="w-4 h-4" /> {a.mode} · {fmtAppt(a)} · {a.status}</p>
                    </div>
                    {a.status === "scheduled" && a.mode !== "walk_in" && (
                      <button onClick={() => attemptJoin(a)} data-testid="portal-join-btn" className="inline-flex items-center gap-1.5 bg-clinical text-white text-sm font-semibold rounded-md px-4 py-2"><Video className="w-4 h-4" /> {tr("Join consultation")}</button>
                    )}
                    {a.status === "completed" && a.feedback_rating && (
                      <div className="flex items-center gap-2 text-sm text-muted2" data-testid="feedback-given">
                        <span>{tr("Your rating:")}</span><StarRating value={a.feedback_rating} readOnly size="w-4 h-4" />
                      </div>
                    )}
                    {a.status === "completed" && !a.feedback_rating && (
                      <FeedbackPrompt appt={a} onDone={load} />
                    )}
                  </div>
                );
              })}
              {appts.length === 0 && <p className="text-muted2">{tr("No appointments scheduled yet.")}</p>}
            </div>
            <div className="mt-8"><PatientRequests /></div>
          </div>
        )}

        {tab === "learn" && (
          <div className="grid gap-6" data-testid="portal-panel-learn">
            <PatientTraining />
            <PatientGroupCalls />
          </div>
        )}

        {tab === "inbox" && (
          <div data-testid="portal-panel-inbox">
            <PatientMessages />
          </div>
        )}

        {tab === "details" && (
          <div data-testid="portal-panel-details">
            <MyDetailsCard />
          </div>
        )}

        {tab === "password" && (
          <div data-testid="portal-panel-password">
            <ChangePasswordCard />
          </div>
        )}

        {tab === "assignments" && (
          <div data-testid="portal-panel-assignments">
            <InterpreterAssignments onJoin={(a) => setJoin(a.id)} />
          </div>
        )}

        {tab === "availability" && (
          <div data-testid="portal-panel-availability">
            <InterpreterAvailability />
          </div>
        )}

        {tab === "translate" && (
          <div data-testid="portal-panel-translate">
            <TranslateTool />
          </div>
        )}

        {tab === "settings" && (
          <div className="grid gap-6" data-testid="portal-panel-settings">
            <NotificationPrefs />
            <TimezoneCard />
          </div>
        )}
      </main>
      <Footer />
      {busy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="clinician-busy-modal">
          <div className="bg-white rounded-xl p-7 max-w-md text-center">
            <div className="w-14 h-14 rounded-full bg-alert/15 flex items-center justify-center mx-auto mb-4">
              <CalendarClock className="w-7 h-7 text-alert" />
            </div>
            <h2 className="font-heading font-bold text-xl text-ink">Please hold on</h2>
            <p className="text-muted2 mt-3 text-sm">{busy.message}</p>
            <p className="text-xs text-clinical font-semibold mt-4 inline-flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-clinical/30 border-t-clinical rounded-full animate-spin" />
              Waiting for the clinician — we'll bring you in automatically…
            </p>
            <button onClick={() => setBusy(null)} data-testid="busy-cancel-btn" className="mt-5 text-sm font-semibold text-muted2 hover:text-ink">Cancel</button>
          </div>
        </div>
      )}
      {join && <JitsiRoom appointmentId={join} onClose={() => { setJoin(null); load(); }} />}
    </div>
  );
}
